import { useEffect, useState } from "react";
import { brandVoiceAPI } from "../../lib/api";
import { Palette, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Header } from '../../components/layout/Header';

const DEFAULT_SYSTEM_PROMPT = `Você é o assistente de conteúdo oficial de Tayane Larini — cantora gospel, autora e mentora de mulheres.

Diretrizes de voz:
• Tom acolhedor e humano antes de qualquer oferta — fale a partir de vivência e fé, não de autoridade de mercado
• Linguagem pessoal e testemunhal — use a própria história (incluindo dor e recomeço) como ponte de identificação
• Espiritualidade natural — linguagem de fé fluida, não institucional nem corporativa
• Autoridade que canta — a música não é produto à parte, é prova viva do propósito
• Direto quando convida — ao apresentar mentoria, livro ou evento, seja claro e prático sem perder delicadeza

Evite:
• Linguagem corporativa ou de "coach"
• Tom comercial agressivo
• Separar artificialmente "cantora" de "mentora" — as identidades se reforçam
• Clichês motivacionais genéricos sem âncora espiritual

A pessoa deve sentir "conheci a Tayane" antes de sentir "recebi uma oferta".`;

const EMPTY = {
    description: "",
    tone_keywords: [],
    avoid_keywords: [],
    example_phrases: [],
    system_prompt: DEFAULT_SYSTEM_PROMPT,
};

export default function BrandVoiceAdmin() {
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tagInput, setTagInput] = useState({ tone: "", avoid: "", phrase: "" });

    useEffect(() => {
        brandVoiceAPI.get()
            .then(r => { if (r.data && r.data.id) setForm({ ...EMPTY, ...r.data }); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await brandVoiceAPI.upsert(form);
            toast.success("Brand Voice salvo — IA usará essas diretrizes");
        } catch { toast.error("Erro ao salvar"); }
        finally { setSaving(false); }
    };

    const addTag = (field, inputKey) => {
        const val = tagInput[inputKey].trim();
        if (!val) return;
        setForm(f => ({ ...f, [field]: [...(f[field] || []), val] }));
        setTagInput(t => ({ ...t, [inputKey]: "" }));
    };

    const removeTag = (field, i) => {
        setForm(f => ({ ...f, [field]: f[field].filter((_, j) => j !== i) }));
    };

    if (loading) return <div className="p-6 text-stone-400 text-sm">Carregando...</div>;

    return (
        <>
        <Header title="Brand Voice" subtitle="Define o tom de voz usado pela IA ao gerar conteúdo" actions={<button onClick={save} disabled={saving} className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50 transition-colors"><Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}</button>} />
        <div className="p-6 space-y-6 max-w-3xl">

            <div className="bg-stone-900 border border-stone-800 rounded-xl p-5 space-y-4">
                {/* Descrição */}
                <div>
                    <label className="text-xs text-stone-400 block mb-1 font-medium">Descrição da voz de marca</label>
                    <textarea rows={3} className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-200 focus:outline-none focus:border-amber-600 resize-none"
                        value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                        placeholder="Descreva em suas palavras como a Tayane se comunica..." />
                </div>

                {/* Tom */}
                <div>
                    <label className="text-xs text-stone-400 block mb-1 font-medium">Palavras que definem o tom</label>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {(form.tone_keywords || []).map((t, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 bg-amber-900/30 text-amber-300 border border-amber-700/50 rounded-full flex items-center gap-1">
                                {t} <button onClick={() => removeTag("tone_keywords", i)} className="hover:text-red-300">×</button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input className="flex-1 bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5 text-xs text-stone-300 focus:outline-none focus:border-amber-600"
                            placeholder="ex: acolhedor, espiritual, testemunhal..."
                            value={tagInput.tone} onChange={e => setTagInput(t => ({...t, tone: e.target.value}))}
                            onKeyDown={e => e.key === "Enter" && addTag("tone_keywords", "tone")} />
                        <button onClick={() => addTag("tone_keywords", "tone")}
                            className="px-3 py-1.5 bg-stone-700 hover:bg-stone-600 text-stone-200 text-xs rounded-lg transition-colors">+</button>
                    </div>
                </div>

                {/* Evitar */}
                <div>
                    <label className="text-xs text-stone-400 block mb-1 font-medium">Palavras e tons a evitar</label>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                        {(form.avoid_keywords || []).map((t, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 bg-red-900/20 text-red-300 border border-red-800/40 rounded-full flex items-center gap-1">
                                {t} <button onClick={() => removeTag("avoid_keywords", i)} className="hover:text-red-100">×</button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input className="flex-1 bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5 text-xs text-stone-300 focus:outline-none focus:border-amber-600"
                            placeholder="ex: coach, corporativo, vendas agressivas..."
                            value={tagInput.avoid} onChange={e => setTagInput(t => ({...t, avoid: e.target.value}))}
                            onKeyDown={e => e.key === "Enter" && addTag("avoid_keywords", "avoid")} />
                        <button onClick={() => addTag("avoid_keywords", "avoid")}
                            className="px-3 py-1.5 bg-stone-700 hover:bg-stone-600 text-stone-200 text-xs rounded-lg transition-colors">+</button>
                    </div>
                </div>

                {/* Frases exemplo */}
                <div>
                    <label className="text-xs text-stone-400 block mb-1 font-medium">Frases de exemplo (no tom certo)</label>
                    <div className="space-y-1.5 mb-2">
                        {(form.example_phrases || []).map((p, i) => (
                            <div key={i} className="flex items-start gap-2 bg-stone-800 rounded-lg px-3 py-2">
                                <span className="text-xs text-stone-300 flex-1">"{p}"</span>
                                <button onClick={() => removeTag("example_phrases", i)} className="text-stone-600 hover:text-red-400 text-xs flex-shrink-0">×</button>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input className="flex-1 bg-stone-800 border border-stone-700 rounded-lg px-3 py-1.5 text-xs text-stone-300 focus:outline-none focus:border-amber-600"
                            placeholder="ex: Deus não desperdiça nenhuma dor que você viveu..."
                            value={tagInput.phrase} onChange={e => setTagInput(t => ({...t, phrase: e.target.value}))}
                            onKeyDown={e => e.key === "Enter" && addTag("example_phrases", "phrase")} />
                        <button onClick={() => addTag("example_phrases", "phrase")}
                            className="px-3 py-1.5 bg-stone-700 hover:bg-stone-600 text-stone-200 text-xs rounded-lg transition-colors">+</button>
                    </div>
                </div>
            </div>

            {/* System prompt da IA */}
            <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
                <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-amber-400" />
                    <label className="text-sm font-medium text-stone-200">System Prompt da IA</label>
                    <span className="text-xs text-stone-500 ml-auto">Instruções completas enviadas à OpenAI</span>
                </div>
                <textarea rows={14} className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-xs text-stone-300 focus:outline-none focus:border-amber-600 resize-none font-mono leading-relaxed"
                    value={form.system_prompt} onChange={e => setForm(f => ({...f, system_prompt: e.target.value}))} />
                <button onClick={() => setForm(f => ({...f, system_prompt: DEFAULT_SYSTEM_PROMPT}))}
                    className="text-xs text-stone-500 hover:text-stone-300 mt-2 transition-colors">
                    ↺ Restaurar prompt padrão
                </button>
            </div>
        </div>
        </>
    );
}
